import React, { useState } from "react";
import { TimesheetNotesModalStyled } from "./timesheet-notes-modal.styles";
import { Button } from "../../../../components/button/button";
import { TextField } from "@mui/material";
import DummyField from "../../../../components/dummy-field/dummy-field";
import TextInputField from "../../../../components/form-fields/text-input-field/text-input-form-field";
import {
  isSomething,
  roundToNearestQuarterHour,
  getCompanyNameById,
} from "../../../../utils/utils";
import {
  CalendarIcon,
  ClockWithPlusIcon,
} from "../../../../utils/svg-components";
import { useQuery } from "@tanstack/react-query";
import { fetchProject, getTime } from "../../../../services/projects-services";
import { calculateProjectTotalFinancialsNew } from "../../../projects-list/project-details/project-health/project-financials/single-project/single-project-totals.util";
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const TimesheetNotesModal = ({
  timesheetModalData,
  onClose,
  companies,
  projectData,
}) => {
  const projectInfo = timesheetModalData?.rowData;

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: () => getTime(),
  });

  const data = calculateProjectTotalFinancialsNew(projectData, users);
  const expenseHours = data?.expense?.hours || 0;
  const budgetedHours = data?.budget?.hours || 0;

  const selectedDate = timesheetModalData?.timeColumn;
  const timeColumn = timesheetModalData?.timeColumn;

  const deliverableName = projectInfo?.deliverable;
  const role = projectInfo?.role;
  const client = projectInfo?.client;

  const flowId = projectInfo?.flowId;
  const projectName = projectInfo?.projectName;

  const weeklyHours = projectInfo?.weeklyHours;

  const weeklyDateObj = weeklyHours?.find((x) => x?.date === selectedDate);

  const loggedMinutes = weeklyDateObj?.minutes;
  const defaultNote = weeklyDateObj?.note;

  const [updatedMinutes, setUpdatedMinutes] = useState(loggedMinutes);
  const [note, setNote] = useState(defaultNote);
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = isSomething(note) || isSomething(updatedMinutes);
  const onSave = async () => {
    if (
      note.trim() !== "" &&
      (!isSomething(updatedMinutes) || updatedMinutes === 0)
    ) {
      alert("Please log time before saving a note.");
      return;
    }

    setIsSaving(true);

    try {
      // Updating the weekly hours with the new data
      await timesheetModalData.handleUpdateWeeklyHours(
        projectInfo?.uniqueId,
        timeColumn,
        updatedMinutes,
        note
      );
      // Close the modal after saving
      onClose();
    } catch (error) {
      console.error("Error saving time entry:", error);
      alert("An error occurred while saving. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  const updateHours = (roundedMinutes) => {
    setUpdatedMinutes(roundedMinutes);
  };

  const clientName = getCompanyNameById(companies, client);

  return (
    <TimesheetNotesModalStyled DialogFooter={<></>}>
      <div className="div-1">
        <div>
          <Typography variant="body2">
            Flow ID: {flowId} | Client: {clientName}
          </Typography>

          <Typography variant="h4" sx={{ marginTop: "3px" }}>
            {projectName}
          </Typography>
        </div>

        <div className="info">
          <div className="internal">
            {deliverableName && (
              <Tooltip title={deliverableName} arrow>
                <div>
                  <DummyField
                    label={"Deliverable"}
                    value={truncateText(deliverableName, 25)}
                  />
                </div>
              </Tooltip>
            )}
            {role && <DummyField label={"Role"} value={role} />}{" "}
          </div>

          <div className="date">
            <DummyField
              label={"Date"}
              value={selectedDate}
              startIcon={<CalendarIcon />}
            />
          </div>
        </div>
      </div>
      <div className="div-2">
        <TextInputField
          isRequired
          multiline
          value={note}
          placeholder={"Note for time entry"}
          rows={9}
          name={"projectDescription"}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </div>
      <div className="div-3">
        <div className="footer">
          <div className="info">
            <DummyField
              noGap
              label={"Time Log"}
              background={"White"}
              startIcon={<ClockWithPlusIcon />}
              endContent={
                <TextField
                  size="small"
                  variant="outlined"
                  defaultValue={
                    loggedMinutes ? (loggedMinutes / 60).toFixed(2) : "0.00"
                  }
                  type="text"
                  onChange={(e) => {
                    let inputHours = parseFloat(e.target.value);

                    // Allow full hour input directly (e.g., 1 for 1.00, 1.25 for 1.25)
                    if (inputHours > 24) {
                      alert("You cannot enter more than 24 hours in a day.");
                      e.target.value = "24.00";
                      inputHours = 24;
                    } else if (inputHours < 0) {
                      alert("You cannot enter negative hours.");
                      e.target.value = "0.00";
                      inputHours = 0;
                    }

                    // Convert hours to minutes for internal storage
                    let inputMinutes = isNaN(inputHours) ? 0 : inputHours * 60;
                    setUpdatedMinutes(inputMinutes);
                  }}
                  onBlur={(e) => {
                    let inputMinutes = updatedMinutes;
                    let roundedMinutes =
                      roundToNearestQuarterHour(inputMinutes);
                    const formattedValue = (roundedMinutes / 60).toFixed(2);
                    e.target.value = formattedValue;

                    // Update the field with the rounded value
                    setUpdatedMinutes(roundedMinutes);
                  }}
                />
              }
            />

            <DummyField
              label={"Act./Est."}
              value={`${expenseHours.toFixed(2)} / ${budgetedHours.toFixed(2)}`}
            />
          </div>
          <div className="actions">
            <Button
              onClick={onClose}
              disabled={false}
              isLoading={false}
              variant="filled"
              color={"primary"}
              sx={{ width: "110px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={!isFormValid}
              isLoading={false}
              sx={{ width: "110px" }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </TimesheetNotesModalStyled>
  );
};

export default TimesheetNotesModal;
